/**
 * Utility helpers for resolving attachment file types.
 * Kept in a separate file so FileTypeIcon.jsx only exports components
 * (required for Vite Fast Refresh / react-refresh).
 */

// ─── Maps ─────────────────────────────────────────────────────────────────────

const MIME_MAP = {
  // Images
  'image/jpeg': 'image',
  'image/jpg': 'image',
  'image/png': 'image',
  'image/webp': 'image',
  'image/gif': 'image',
  'image/svg+xml': 'image',
  'image/bmp': 'image',
  'image/tiff': 'image',
  'image/heic': 'image',
  // PDF
  'application/pdf': 'pdf',
  // Word
  'application/msword': 'word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'word',
  'application/rtf': 'text',
  'text/rtf': 'text',
  // Excel / CSV
  'application/vnd.ms-excel': 'excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
  'text/csv': 'csv',
  'application/csv': 'csv',
  // PowerPoint
  'application/vnd.ms-powerpoint': 'powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'powerpoint',
  // Text
  'text/plain': 'text',
  // Archives
  'application/zip': 'archive',
  'application/x-zip-compressed': 'archive',
  'application/x-rar-compressed': 'archive',
  'application/vnd.rar': 'archive',
  'application/x-7z-compressed': 'archive',
  'application/x-tar': 'archive',
  'application/gzip': 'archive',
  'application/x-gzip': 'archive',
  // Video
  'video/mp4': 'video',
  'video/quicktime': 'video',
  'video/x-msvideo': 'video',
  'video/x-matroska': 'video',
  'video/webm': 'video',
  // Audio
  'audio/mpeg': 'audio',
  'audio/mp3': 'audio',
  'audio/wav': 'audio',
  'audio/x-wav': 'audio',
  'audio/aac': 'audio',
  'audio/mp4': 'audio',
  'audio/x-m4a': 'audio',
};

const EXT_MAP = {
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  webp: 'image',
  gif: 'image',
  svg: 'image',
  bmp: 'image',
  tiff: 'image',
  heic: 'image',
  pdf: 'pdf',
  doc: 'word',
  docx: 'word',
  rtf: 'text',
  xls: 'excel',
  xlsx: 'excel',
  csv: 'csv',
  ppt: 'powerpoint',
  pptx: 'powerpoint',
  txt: 'text',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  mp4: 'video',
  mov: 'video',
  avi: 'video',
  mkv: 'video',
  webm: 'video',
  mp3: 'audio',
  wav: 'audio',
  aac: 'audio',
  m4a: 'audio',
};

export const TYPE_LABELS = {
  image: 'Image file',
  pdf: 'PDF file',
  word: 'Word document',
  excel: 'Excel spreadsheet',
  csv: 'CSV spreadsheet',
  powerpoint: 'PowerPoint presentation',
  text: 'Text file',
  archive: 'Archive file',
  video: 'Video file',
  audio: 'Audio file',
  link: 'External link',
  unknown: 'Unknown file type',
};

/**
 * Resolves the attachment type from MIME type (first) or file extension (fallback).
 * Handles compound extensions such as `.tar.gz`.
 *
 * @param {string} [mimeType]
 * @param {string} [fileName]
 * @returns {{ type: string, label: string }}
 */
export function getAttachmentFileType(mimeType, fileName) {
  if (mimeType) {
    const normalised = mimeType.toLowerCase().split(';')[0].trim();
    if (MIME_MAP[normalised]) {
      const type = MIME_MAP[normalised];
      return { type, label: TYPE_LABELS[type] };
    }
    if (normalised === 'link') {
      return { type: 'link', label: TYPE_LABELS.link };
    }
  }

  if (fileName) {
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.tar.gz') || lower.endsWith('.tar.bz2')) {
      return { type: 'archive', label: TYPE_LABELS.archive };
    }
    const ext = lower.split('.').pop();
    if (ext && EXT_MAP[ext]) {
      const type = EXT_MAP[ext];
      return { type, label: TYPE_LABELS[type] };
    }
  }

  return { type: 'unknown', label: TYPE_LABELS.unknown };
}
